import React, { useRef, useState, useCallback, useEffect } from 'react';
import HttpRequest from '../http-service';
import { Icon } from '@iconify/react';
import countries from '../assets/database/countries';

function Contact() {
    const form = useRef();
    const [isLoading, setIsLoading] = useState(false);
    const [fullname, setFullname] = useState('');
    const [email, setEmail] = useState('');
    const [contact, setContact] = useState('');
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [countryOptionsVisible, setCountryOptionsVisible] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    
    // Notification state
    const [notification, setNotification] = useState({
        show: false,
        type: '',
        message: ''
    });

    // Auto-hide notification after 4 seconds
    useEffect(() => {
        if (notification.show) {
            const timer = setTimeout(() => {
                setNotification({ show: false, type: '', message: '' });
            }, 4000);
            return () => clearTimeout(timer);
        }
    }, [notification.show]);

    const showNotification = (type, message) => {
        setNotification({ show: true, type, message });
    };

    const closeNotification = () => {
        setNotification({ show: false, type: '', message: '' });
    };

    const resetForm = () => {
        setFullname('');
        setEmail('');
        setContact('');
        setSubject('');
        setMessage('');
        setSelectedCountry(null);
        if (form.current) {
            form.current.reset();
        }
    };

    const handleFormSubmit = async (event) => {
        event.preventDefault();
        setIsLoading(true);

        try {
            // Single API call that handles both database save and email
            const response = await HttpRequest.post('/contact', { 
                name: fullname, 
                email, 
                phone: contact, 
                subject, 
                message 
            });
            
            console.log('✅ Contact form response:', response.data);
            
            // Check if the response indicates success
            if (response.data.success) {
                showNotification('success', response.data.message || 'Message sent successfully! Check your email for confirmation.');
                resetForm();
            } else {
                // Server returned success: false
                throw new Error(response.data.message || 'Failed to send message');
            }
            
        } catch (err) {
            console.error('❌ Form submission error:', err);
            
            // Determine error message
            let errorMessage = 'Failed to send message. Please try again.';
            
            if (err.response?.data?.message) {
                // Server returned an error message
                errorMessage = err.response.data.message;
            } else if (err.message && !err.message.includes('Network')) {
                // Custom error message from our throw above
                errorMessage = err.message;
            } else if (err.code === 'ECONNABORTED') {
                errorMessage = 'Request timed out. Please check your connection.';
            } else if (!err.response) {
                errorMessage = 'Server is unavailable. Please try again later.';
            }
            
            showNotification('error', errorMessage);
            
            // DO NOT reset form on error - let user retry
        } finally {
            setIsLoading(false);
        }
    };

    const handleCountrySelect = useCallback((country) => {
        setSelectedCountry(country);
        setContact(`+${country.phone}`);
        setCountryOptionsVisible(false);
        setSearchQuery('');
    }, []);

    const filteredCountries = countries.filter((country) =>
        country.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const toggleCountryOptions = useCallback(() => {
        setCountryOptionsVisible(prev => !prev);
    }, []);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (countryOptionsVisible && !event.target.closest('.select-box')) {
                setCountryOptionsVisible(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [countryOptionsVisible]);

    return (
        <section className="contact" id="contact">
            <h2 className="heading">Get In <span>Touch</span></h2>
            <p>
                Have a project in mind or want to discuss opportunities? I would love to hear from you! 
                Feel free to reach out via the form below or connect with me on social media.
            </p>
            
            <form ref={form} onSubmit={handleFormSubmit}>
                <div className="input-box">
                    <input 
                        type="text" 
                        name="name" 
                        value={fullname} 
                        onChange={(e) => setFullname(e.target.value)} 
                        placeholder="Full Name *" 
                        required
                        aria-label="Full Name"
                        disabled={isLoading}
                    />
                    <input 
                        type="email" 
                        name="email" 
                        value={email} 
                        onChange={(e) => setEmail(e.target.value)} 
                        placeholder="Email Address *" 
                        required
                        aria-label="Email Address"
                        disabled={isLoading}
                    />
                </div>
                <div className="input-box">
                    <div className="select-box">
                        <div className="selected-option">
                            <div 
                                className="country-container" 
                                onClick={toggleCountryOptions}
                                role="button"
                                tabIndex={0}
                                aria-label={`Select country code. Currently selected: ${selectedCountry ? selectedCountry.name : 'None'}`}
                                aria-expanded={countryOptionsVisible}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        toggleCountryOptions();
                                    }
                                    if (e.key === 'Escape') {
                                        setCountryOptionsVisible(false);
                                    }
                                }}
                            >
                                <span className="iconify" id="world">
                                    <Icon icon={
                                        selectedCountry
                                            ? `flag:${selectedCountry.code.toLowerCase()}-4x3`
                                            : 'twemoji:globe-showing-asia-australia'
                                    } />
                                </span>
                                <strong>{selectedCountry ? `${selectedCountry.code}` : "Code"}</strong>
                            </div>
                            <input 
                                type="tel" 
                                name="phone" 
                                id="contactNumber" 
                                value={contact} 
                                onChange={(e) => setContact(e.target.value)} 
                                placeholder="Phone Number"
                                aria-label="Phone Number"
                                disabled={isLoading}
                            />
                        </div>
                        {countryOptionsVisible && (
                            <div 
                                className="options active"
                                role="listbox"
                                aria-label="Country list"
                            >
                                <input
                                    type="text"
                                    className="search-box"
                                    placeholder="Search country..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    aria-label="Search countries"
                                    autoFocus
                                />
                                <ul>
                                    {filteredCountries.map((country) => (
                                        <li
                                            key={country.code}
                                            role="option"
                                            aria-selected={selectedCountry?.code === country.code}
                                            className={`option ${selectedCountry?.code === country.code ? 'selected' : ''}`}
                                            onClick={() => handleCountrySelect(country)}
                                            tabIndex={0}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    handleCountrySelect(country);
                                                }
                                            }}
                                        >
                                            <div>
                                                <span className="iconify">
                                                    <Icon icon={`flag:${country.code.toLowerCase()}-4x3`} />
                                                </span>
                                                <span className="country-name">{country.name}</span>
                                            </div>
                                            <strong>+{country.phone}</strong>
                                        </li>
                                    ))}
                                    {filteredCountries.length === 0 && (
                                        <li className="no-results">No countries found</li>
                                    )}
                                </ul>
                            </div>
                        )}
                    </div>
                    <input 
                        type="text" 
                        name="subject" 
                        value={subject} 
                        onChange={(e) => setSubject(e.target.value)} 
                        placeholder="Subject *" 
                        required
                        aria-label="Subject"
                        disabled={isLoading}
                    />
                </div>
                <textarea 
                    name="message" 
                    value={message} 
                    onChange={(e) => setMessage(e.target.value)} 
                    cols="30" 
                    rows="10" 
                    placeholder="Your Message *"
                    required
                    aria-label="Your message"
                    disabled={isLoading}
                ></textarea>
                <button 
                    type="submit" 
                    className={`btn ${isLoading ? 'loading' : ''}`}
                    disabled={isLoading}
                >
                    {isLoading ? 'Sending...' : 'Send Message'}
                </button>
            </form>

            {/* Notification Popup */}
            <div 
                className={`contact-notification ${notification.show ? 'active' : ''} ${notification.type}`}
                onClick={closeNotification}
            >
                <div className="contact-notification-content" onClick={(e) => e.stopPropagation()}>
                    <div className="notification-icon">
                        {notification.type === 'success' ? (
                            <div className="icon-circle success">
                                <i className='bx bx-check'></i>
                            </div>
                        ) : (
                            <div className="icon-circle error">
                                <i className='bx bx-x'></i>
                            </div>
                        )}
                    </div>
                    <h3>{notification.type === 'success' ? 'Success!' : 'Oops!'}</h3>
                    <p>{notification.message}</p>
                    <div className="notification-progress">
                        <div className={`progress-bar ${notification.show ? 'animate' : ''}`}></div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default Contact;